import type React from 'react';

import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';

import type { StackedBarChartData } from '~/utils/visualization-calculators/cashflow-aggregator';

interface StackedRevenueChartProps {
  data: StackedBarChartData[];
  series: Array<{ key: string; name: string; color: string }>;
  width: number;
  height: number;
  showGrid?: boolean;
  showLegend?: boolean;
}

export const StackedRevenueChart: React.FC<StackedRevenueChartProps> = ({
  data,
  series,
  width,
  height,
  showGrid = true,
  showLegend = true,
}) => {
  // Custom tooltip to format currency values
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{
      name: string;
      value: number;
      color: string;
    }>;
    label?: string | number;
  }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            padding: '8px',
          }}
        >
          <p style={{ margin: 0, fontWeight: 'bold', fontSize: '12px' }}>Year {label}</p>
          {payload.map((entry) => (
            <p
              key={entry.name}
              style={{
                margin: '2px 0',
                fontSize: '11px',
                color: entry.color,
              }}
            >
              {entry.name}: ${(entry.value / 1000000).toFixed(2)}M
            </p>
          ))}
          <p
            style={{
              margin: '4px 0 0 0',
              paddingTop: '4px',
              borderTop: '1px solid #E2E8F0',
              fontSize: '11px',
              fontWeight: 'bold',
            }}
          >
            Total: $
            {(payload.reduce((sum: number, entry) => sum + entry.value, 0) / 1000000).toFixed(2)}M
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <BarChart
      width={width}
      height={height}
      data={data}
      margin={{ top: 10, right: 30, left: 30, bottom: 20 }}
    >
      {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" opacity={0.5} />}
      <XAxis
        dataKey="year"
        tick={{ fontSize: 12 }}
        tickFormatter={(value) => value.toString()}
        label={{ value: 'Year', position: 'insideBottom', offset: -10 }}
      />
      <YAxis
        tick={{ fontSize: 12 }}
        tickFormatter={(value) => `$${(value / 1000000).toFixed(0)}M`}
        label={{ value: 'Payment Amount', angle: -90, position: 'insideLeft', offset: -10 }}
      />
      <Tooltip content={<CustomTooltip />} />
      {showLegend && <Legend wrapperStyle={{ fontSize: '12px' }} verticalAlign="top" height={36} />}
      {series.map((s) => (
        <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} stackId="a" />
      ))}
    </BarChart>
  );
};
